import clsx from 'clsx';
import { useField } from 'formik';
import React from 'react';

import {
  Box,
  ClassNameMap,
  MenuItem,
  SelectProps as MuiSelectProps,
  OutlinedInputClassKey,
  SelectClassKey,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';

import { BaseTextField, BaseTextFieldProps } from '../BaseTextField';
import isNil from 'lodash/isNil';


const useStyles = makeStyles<{ label?: React.ReactNode; classes?: TextFieldProps['classes'] }>({ name: 'UpliftSelect' })((theme) => ({
  formControl: {
    '& [data-detail-label]': {
      display: 'none',
    },
  },
  menuItem: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  itemText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  image: {
    width: 80,
    maxHeight: 30,
    objectFit: 'contain',
    marginLeft: theme.spacing(1),
  },
}));


export const SelectItem = withStyles(MenuItem, theme => ({
  root: {
    display: 'flex',
    alignItems: 'space-between',
    overflow: 'hidden',
    width: '100%',
    '&.Mui-selected': {
      backgroundColor: theme.palette.secondary.background,

      '&:hover': {
        backgroundColor: theme.palette.secondary.background,
      },
    },
  },
}));


export type SelectOption = {
  value: string;
  label?: string;
  disabled?: boolean;
  detailLabel?: string;
  logo?: string;
};


export type SelectProps = BaseTextFieldProps & {
  name: string;
  items?: SelectOption[];
  MenuProps?: MuiSelectProps['MenuProps'];
  autoWidth?: boolean;
  selectClasses?: Partial<ClassNameMap<SelectClassKey>>;
  inputClasses?: Partial<ClassNameMap<OutlinedInputClassKey>>;
  testId?: string;
  displayEmpty?: boolean;
};


export const SelectRaw: React.FC<SelectProps> = ({
  name,
  className,
  items,
  label,
  disabled,
  autoFocus,
  placeholder,
  children,
  MenuProps,
  autoWidth,

  error,
  helperText,

  onChange,
  onBlur,
  value,
  classes: _classes,
  selectClasses,
  inputClasses,
  color,
  fullWidth = true,
  testId = 'select',
  displayEmpty = false,
  ...props
}) => {
  const { classes } = useStyles({ label: label, classes: _classes });

  return (
    <BaseTextField
      select
      data-testid={testId}
      {...props}
      fullWidth={fullWidth}
      name={name}
      label={label}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={onChange}
      onBlur={onBlur}
      value={value ?? ''}
      error={error}
      helperText={helperText}
      inputClasses={inputClasses}

      className={clsx(className, classes.formControl)}
      color={color}

      slotProps={{
        ...props.slotProps,
        select: {
          ...props.slotProps?.select,
          displayEmpty: displayEmpty,
          color: color,
          autoWidth: autoWidth,
          classes: selectClasses,
          MenuProps: {
            color: 'secondary',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            ...MenuProps,
          }
        }
      }}
    >
      {placeholder && (
        <MenuItem disabled value="" data-testid="select-placeholder">
          <Typography color="textSecondary" component="i">
            {placeholder}
          </Typography>
        </MenuItem>
      )}
      {children && children}
      {!children && items?.map(item => (
        <SelectItem key={item.value} value={item.value} disabled={item.disabled} data-testid="select-menu-item" color="secondary">
          <Box className={classes.menuItem}>
            <span className={classes.itemText}>
              {item.label}
            </span>
            {item.logo && (
              <img
                className={classes.image}
                src={item.logo}
                alt={`${item.value}-logo`}
              />
            )}
            {item.detailLabel && (
              <Typography variant="caption" color="textSecondary" component="div" data-detail-label data-testid="select-menu-item-details">
                {item.detailLabel}
              </Typography>
            )}
          </Box>
        </SelectItem>
      ))}
    </BaseTextField>
  );
};


export const Select: React.FC<Omit<SelectProps, 'onChange' | 'onBlur' | 'value' | 'error'>> = (props) => {
  const [ field, meta ] = useField(props.name);
  const hasError = Boolean(!isNil(meta.error) && meta.touched);

  return (
    <SelectRaw
      {...props}

      error={hasError}
      helperText={hasError ? meta.error : props.helperText}

      onChange={field.onChange}
      onBlur={field.onBlur}
      value={field.value}
      name={field.name}
    />
  );
};
